/**
 * RESPONSIBILITY: the Contact page — its words, and what the questionnaire asks.
 *
 * The QUESTIONS are content, not markup: "I would like to" is a list of tick-boxes that
 * the church will want to change without a deploy, so it is a @StringList and the form
 * draws itself from it.
 *
 * Where a submission GOES is the app's own endpoint. The framework has no opinion about
 * it, which is why createApp takes appRoutes.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text, Paragraph, StringList, Link } from '@framework/core/decorators';
import { SectionName } from '../section-names';
import { ABOVE_HINT, LINK_HINT } from '../hints';

/** The four questions below are the form itself, so their options need saying once. */
const ANSWERS_HINT = 'The answers this question offers. Adding one changes the form.';

@Section({ name: SectionName.Contact, label: 'Contact page' })
export class ContactSection {
  @Text({
    label: 'Subtitle', maxLength: 120, default: 'About',
    group: 'Page head', description: ABOVE_HINT,
  })
  eyebrow!: string;
  @Text({ label: 'Title', maxLength: 300, default: 'Contact Us', group: 'Page head' })
  heading!: string;
  @Paragraph({ label: 'Description', maxLength: 4000, rows: 3, group: 'Page head' })
  intro!: string;

  @StringList({
    label: 'Which best describes you?', maxItems: 10, description: ANSWERS_HINT,
    default: ["I'm visiting for the first time", "I've visited a few times", "I'm a member of ACFI", 'Something else'],
  })
  describesYou!: string[];

  @StringList({
    label: 'I would like to…', maxItems: 16, description: ANSWERS_HINT,
    default: [
      'Plan a first visit', "Ask about children's ministry", 'Submit a prayer request',
      'Speak with a pastor', 'Join a Bible study', 'Serve or volunteer',
      'Ask about membership', 'Something else',
    ],
  })
  iWouldLikeTo!: string[];

  @StringList({
    label: 'How did you hear about us?', maxItems: 10, description: ANSWERS_HINT,
    default: ['A friend or family member', 'Search or the website', 'Social media', 'A church event', 'Other'],
  })
  heardAboutUs!: string[];

  @StringList({
    label: 'How should we reply?', maxItems: 6, description: ANSWERS_HINT,
    default: ['Email', 'Phone call', 'Text message', 'No reply needed'],
  })
  replyBy!: string[];

  @Text({
    label: 'Question 1', maxLength: 300, default: 'Which best describes you?',
    group: 'Questions asked', collapsed: true,
    description: 'The wording above each set of answers. The answers themselves are the lists above.',
  })
  describesYouQuestion!: string;
  @Text({ label: 'Question 2', maxLength: 300, default: 'I would like to…', group: 'Questions asked' })
  iWouldLikeToQuestion!: string;
  @Text({
    label: 'Question 3', maxLength: 300, default: 'How did you hear about us?',
    group: 'Questions asked',
  })
  heardAboutUsQuestion!: string;
  @Text({
    label: 'Question 4', maxLength: 300, default: 'How should we reply?',
    group: 'Questions asked',
  })
  replyByQuestion!: string;

  @Text({ label: 'First name', maxLength: 120, default: 'First name', group: 'Form fields', collapsed: true })
  firstNameLabel!: string;
  @Text({ label: 'Last name', maxLength: 120, default: 'Last name', group: 'Form fields' })
  lastNameLabel!: string;
  @Text({ label: 'Email', maxLength: 120, default: 'Email', group: 'Form fields' })
  emailLabel!: string;
  @Text({ label: 'Phone', maxLength: 120, default: 'Phone', group: 'Form fields' })
  phoneLabel!: string;
  @Text({ label: 'Message', maxLength: 120, default: 'Your message', group: 'Form fields' })
  messageLabel!: string;
  @Text({
    label: 'Message box prompt', maxLength: 300, group: 'Form fields',
    default: 'Anything you would like us to know before we reply',
  })
  messagePlaceholder!: string;

  @Text({ label: 'Button text', maxLength: 120, default: 'Send', group: 'Form' })
  sendLabel!: string;
  @Text({
    label: 'Button text while sending', maxLength: 120, default: 'Sending…', group: 'Form',
  })
  sendingLabel!: string;
  @Text({
    label: 'Thank-you title', maxLength: 300, default: 'Thank you', group: 'Form',
  })
  thanksHeading!: string;
  @Paragraph({
    label: 'When it does not send', maxLength: 600, rows: 2, group: 'Form',
    default: 'That did not send. Please try again, or email us directly.',
  })
  failedMessage!: string;
  @Paragraph({
    label: 'Thank-you message', maxLength: 4000, rows: 2, group: 'Form',
    description: 'Shown in place of the form once it has been sent.',
  })
  thanks!: string;

  /**
   * THE DETAILS LIVE HERE, where they are shown. They were on the Footer, which never
   * displays them — this page was their only reader — so the pencil beside them had to
   * open the whole footer form, and an editor who wanted to change an email address got
   * nav links and social icons. Each is optional: empty, its row is not drawn.
   */
  @Text({
    label: 'Email address', maxLength: 300, group: 'Aside', collapsed: true,
    description: 'Shown beside the form. Leave empty to leave it off.',
  })
  email!: string;
  @Text({ label: 'Phone number', maxLength: 120, group: 'Aside', description: 'Leave empty to leave it off.' })
  phone!: string;
  @Text({
    label: 'Prayer requests email address', maxLength: 300, group: 'Aside',
    description: 'Leave empty to leave it off.',
  })
  prayerEmail!: string;

  @Text({
    label: 'Email heading', maxLength: 120, default: 'Email', group: 'Aside',
    description: 'The three headings above those details.',
  })
  emailHeading!: string;
  @Text({ label: 'Phone heading', maxLength: 120, default: 'Phone', group: 'Aside' })
  phoneHeading!: string;
  @Text({ label: 'Prayer heading', maxLength: 120, default: 'Prayer requests', group: 'Aside' })
  prayerHeading!: string;

  @Text({ label: 'Button text', maxLength: 120, default: 'Plan a visit', group: 'Aside' })
  visitLabel!: string;
  @Link({ label: 'Button link', group: 'Aside', description: LINK_HINT })
  visitHref!: string;
}
